import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  ShoppingCartIcon,
  UserIcon,
  TicketIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { isGuest } from '../utils/roleUtils';

// Create a typed icon component
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface ActionItem {
  id: string;
  type: 'drinks' | 'profile' | 'documents' | 'guest_cards';
  title: string;
  description: string;
  count?: number;
  icon: React.ReactNode;
  color: string;
  route: string;
}

const formatCurrency = (amount: number) => new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
}).format(amount);

interface ActionRequiredWidgetProps {
  className?: string;
  /** Meldet dem Elternteil, wie viele offene Aufgaben es gibt (0 = Widget rendert nichts). */
  onCountChange?: (count: number) => void;
}

const ActionRequiredWidget: React.FC<ActionRequiredWidgetProps> = ({ className = '', onCountChange }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const isGuestUser = isGuest(user);

  useEffect(() => {
    fetchActionItems();
  }, []);

  // Elternteil über die Anzahl offener Aufgaben informieren (für das Layout).
  useEffect(() => {
    if (!loading) onCountChange?.(actions.length);
  }, [actions, loading, onCountChange]);

  const fetchActionItems = async () => {
    setLoading(true);
    const actionItems: ActionItem[] = [];

    try {
      // 1. Check unbezahlte Verzehrkosten
      const drinksRes = await apiClient.get('/api/drinks/mydrinks');
      const unpaidDrinks = (drinksRes.data || []).filter((d: any) => d.paid === 0);
      if (unpaidDrinks.length > 0) {
        const totalAmount = unpaidDrinks.reduce((sum: number, d: any) => sum + parseFloat(d.amount || 0), 0);
        actionItems.push({
          id: 'drinks',
          type: 'drinks',
          title: 'Offene Verzehrkosten',
          description: `${unpaidDrinks.length} unbezahlt${unpaidDrinks.length === 1 ? 'e Position' : 'e Positionen'} (${formatCurrency(totalAmount)})`,
          count: unpaidDrinks.length,
          icon: <ShoppingCartIcon className="h-5 w-5" />,
          color: 'text-warning-600 dark:text-warning-400',
          route: '/my-drinks'
        });
      }

      // 2. Check unvollständiges Profil
      const profileRes = await apiClient.get('/api/members/profile');
      const profileData = profileRes.data;
      // Gäste haben kein Eintrittsdatum - nur Basisdaten prüfen
      const requiredFieldsMap = isGuestUser
        ? { vorname: 'Vorname', name: 'Nachname', email: 'E-Mail', strasse: 'Straße' }
        : { vorname: 'Vorname', name: 'Nachname', email: 'E-Mail', strasse: 'Straße', eintritt: 'Eintrittsdatum' };

      const missingFieldsList: string[] = [];
      Object.entries(requiredFieldsMap).forEach(([field, label]) => {
        if (!profileData[field] || profileData[field] === '') {
          missingFieldsList.push(label);
        }
      });

      const hasPhoto = !!profileData.foto_path;
      if (!hasPhoto) {
        missingFieldsList.push('Profilbild');
      }

      if (missingFieldsList.length > 0) {
        const missingText = missingFieldsList.join(', ');
        actionItems.push({
          id: 'profile',
          type: 'profile',
          title: 'Profil unvollständig',
          description: `Fehlend: ${missingText}`,
          count: missingFieldsList.length,
          icon: <UserIcon className="h-5 w-5" />,
          color: 'text-warning-600 dark:text-warning-400',
          route: '/profile'
        });
      }

      // 3. Check fehlende Hundedokumente (für alle Benutzer)
      const dogsRes = await apiClient.get('/api/dogs/get_user_dogs');
      if (dogsRes.data.success) {
        const dogs = dogsRes.data.dogs || [];
        let totalMissingDocs = 0;

        for (const dog of dogs) {
          const dogId = dog.hunde_id || dog.id;
          if (dogId) {
            try {
              const docCheckRes = await apiClient.get(`/api/dogs/check_required_documents/${dogId}`);
              const missingDocs = docCheckRes.data.missing_documents || [];
              totalMissingDocs += missingDocs.length;
            } catch (err) {
              console.error(`Error checking documents for dog ${dogId}:`, err);
            }
          }
        }

        if (totalMissingDocs > 0) {
          actionItems.push({
            id: 'documents',
            type: 'documents',
            title: 'Fehlende Hundedokumente',
            description: `${totalMissingDocs} ${totalMissingDocs === 1 ? 'Dokument fehlt' : 'Dokumente fehlen'}`,
            count: totalMissingDocs,
            icon: <DogIcon className="h-5 w-5" />,
            color: 'text-warning-600 dark:text-warning-400',
            route: '/my-dogs'
          });
        }
      }

      // 4. Check unbezahlte Gästekarten (nur für Gäste)
      if (isGuestUser) {
        const guestCardsRes = await apiClient.get('/api/guest-cards/my-cards');
        const myCards = guestCardsRes.data.cards || [];
        const unpaidCards = myCards.filter((card: any) => !card.bezahlt_am);

        if (unpaidCards.length > 0) {
          const totalAmount = unpaidCards.reduce((sum: number, card: any) => sum + (card.price || 0), 0);
          actionItems.push({
            id: 'guest_cards',
            type: 'guest_cards',
            title: 'Unbezahlte Gästekarten',
          description: `${unpaidCards.length} ${unpaidCards.length === 1 ? 'Karte' : 'Karten'} (${formatCurrency(totalAmount)})`,
            count: unpaidCards.length,
            icon: <TicketIcon className="h-5 w-5" />,
            color: 'text-warning-600 dark:text-warning-400',
            route: '/my-guest-cards'
          });
        }
      }

      setActions(actionItems);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching action items:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (route: string) => {
    navigate(route);
  };

  // Zeige Widget nicht an während des Ladens oder wenn keine Aktionen erforderlich sind
  if (loading || actions.length === 0) {
    return null;
  }

  return (
    <div className={`rounded-xl border border-warning-300 bg-warning-50/60 p-3 shadow-sm dark:border-warning-700/60 dark:bg-warning-900/10 ${className}`}>
      <div className="mb-2 flex items-center gap-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-900/20">
          <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        </div>
        <h2 className="text-sm font-bold text-gray-900 dark:text-white">
          Noch zu erledigen
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-2">
        {actions.map((action) => (
          <button
            key={action.id}
            onClick={() => handleActionClick(action.route)}
            className="w-full flex items-center justify-between p-2.5 rounded-lg border border-warning-200/70 dark:border-gray-700/30 bg-white dark:bg-[#1a2332]
                      hover:shadow-md hover:border-amber-300 dark:hover:border-amber-500/50
                      transition-all duration-200"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div className={`${action.color}`}>
                {action.icon}
              </div>
              <div className="min-w-0 text-left">
                <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                  {action.title}
                </p>
                <p className="truncate text-xs text-gray-600 dark:text-gray-400">
                  {action.description}
                </p>
              </div>
            </div>
            <ChevronRightIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ActionRequiredWidget;
